import React from 'react'
import { useLoginSysAdmin } from '../../../../../../role/hook/login-sys-admin'
import { useRoleOrgGroup, useRoleOrgNode, useRoleOrgNodeGroup, useRoleOrgNodeUser, useRoleOrgUser } from '../../../../../../role/hook/role-org'
import { useFeatureAI } from '../../../../../../shared/component/org-feature'
import { useAuditCapabilities } from '../../../../../../shared/audit/use-audit-capabilities'
import { contentAuditSearch } from '../../../../../../shared/audit/filters-model'
import { Menu, MenuItem, MenuItemHeader } from '../../../../../../shared/menu/menu'
import {
  IconActivityLog,
  IconAdd,
  IconAiDocument,
  IconCopy,
  IconCourseLaunch,
  IconDelete,
  IconDetails,
  IconEdit,
  IconFile,
  IconGroup,
  IconLink,
  IconMove,
  IconPlus,
  IconPublishing,
  IconRecycle,
  IconUser
} from '../../../../../../shared/icon/icon'
import { useMetadataTypeIsCaffeine } from '../hooks/hooks'
import { useCourseActionsHost } from './course-actions-host'

// ONE definition of what can be done to a node -- a course, a folder
// (category), a template, a library item -- and in what order, under which
// names and for whom. Every ⋮ menu for a node is built from it: the node's
// own page header (and its sub-pages: edit, move, delete, ...) and its card
// in the grid. They used to be two hand-written lists per kind that drifted
// apart (owner 2026-10-07: the course page and the course card offered
// different actions).
//
// The only difference between the two places is presentation: a page header
// shows the items marked `button` (Launch, Edit, Copy, Delete) as buttons
// beside the ⋮ and the rest in it; a card shows the items marked `card`
// (Launch) as small square icon buttons beside its ⋮, in this order, and
// the rest in it. Either way an item is shown once: a pinned item is not
// also in that ⋮. At phone width a header folds its buttons into the ⋮ too,
// except the one marked `primary` (a course's Launch), which stays a button
// so it is never hidden; a card keeps its pinned buttons. Pinning another
// action on cards is one `card: true` in its definition below.
//
// The visibility rules are today's, unchanged (an after-release change will
// revisit the super-admin shortcuts). Each `url` is the node's own page: the
// course's, the folder's grid, the template's or the library item's; every
// action is a route under it, so the same item works from the card and from
// the page.

export const WORDS = {
  internal: 'Internal tools',
  organization: 'Organization',
  sysEdit: 'Sys edit',
  assets: 'Assets',
  // The staff list of the course's versions: each one's metadata snapshot
  // (node-course-version), which one is current, and the JSON to edit.
  versionMetadata: 'Version metadata',
  addNode: 'Add node',
  addUnityPackage: 'Add Unity package',
  details: 'Details',
  edit: 'Edit',
  newCourse: 'New course',
  addCategory: 'Add category',
  userPermissions: 'User permissions',
  groupPermissions: 'Group permissions',
  deepLink: 'Deep link',
  // The web viewer: sign in, install or update, then launch the course.
  launch: 'Launch',
  move: 'Move',
  copy: 'Copy',
  ai: 'AI',
  newTemplate: 'New template',
  // The node's files (publish-asset): each file's active version, its
  // uploads, who uploaded them and their notes.
  fileVersions: 'File versions',
  recycleBin: 'Recycle bin',
  // The node's own History (everything under it), on its page.
  history: 'History',
  // A section's top: the Audit log, on all content.
  auditLog: 'Audit log',
  delete: 'Delete'
}

const heading = (id, text, show) => ({ id, heading: text, show: !!show })
const action = (id, text, Icon, props) => ({ id, text, Icon, ...props, show: !!props.show })

// A node's "History" opens its page's History drawer (?history=1, the
// breadcrumb bar's History): everything under the node. Offered to whoever
// that History opens for -- the audit log (not the learning-activity
// level), or the update right on content (history-button.js).
const useHistoryAction = url => {
  const caps = useAuditCapabilities()
  const roleOrgNode = useRoleOrgNode()
  const show = (caps.canAudit && !caps.activityLog) || !!roleOrgNode.accessUpdate
  return action('history', WORDS.history, IconActivityLog, { to: `${url}?history=1`, show })
}

// The organization's per-node permission pages, each behind the reader's
// access to both the list and the node's grants.
const usePermissionPages = () => {
  const roleOrgUser = useRoleOrgUser()
  const roleOrgNodeUser = useRoleOrgNodeUser()
  const roleOrgGroup = useRoleOrgGroup()
  const roleOrgNodeGroup = useRoleOrgNodeGroup()

  return {
    users: !!(roleOrgUser.accessRead && roleOrgNodeUser.accessRead),
    groups: !!(roleOrgGroup.accessRead && roleOrgNodeGroup.accessRead)
  }
}

/// ///////////////////////////////////////////////////////////////////////
// Course (and streaming video): url = the course's page.

export const useCourseActions = ({ url, node, nodeID }) => {
  const sysAdmin = useLoginSysAdmin()
  const roleOrgNode = useRoleOrgNode()
  const permissions = usePermissionPages()
  const featureAI = useFeatureAI()
  const isCaffeine = useMetadataTypeIsCaffeine(node)
  const host = useCourseActionsHost()
  const history = useHistoryAction(url)

  const id = nodeID || (node && node.id)

  // Never a dead button (owner 2026-10-08): a course known to have no web
  // build offers no Launch (its card's platform icons say where it runs). When
  // the check could not tell (it failed), Launch is shown and enabled.
  const noWebBuild = !!id && host.hasWebBuild(id) === false

  return [
    // The course's primary action, first: the full web launch (sign in,
    // install or update, then launch) in a new tab, called straight from the
    // click so the tab opens inside it. A button in the page header, first
    // in its row and far from Delete; the rocket beside the card's ⋮.
    action('launch', WORDS.launch, IconCourseLaunch, {
      onClick: () => host.launch(id),
      disabled: host.launching,
      show: host.canLaunch && !!id && !noWebBuild,
      button: true,
      primary: true,
      card: true
    }),

    heading('internal', WORDS.internal, sysAdmin),
    action('sys-edit', WORDS.sysEdit, IconEdit, { to: `${url}/edit-sys`, show: sysAdmin }),
    action('assets', WORDS.assets, IconFile, { to: `${url}/edit-sys/assets`, show: sysAdmin }),
    action('version-metadata', WORDS.versionMetadata, IconFile, { to: `${url}/edit-sys/versions`, show: sysAdmin }),

    heading('organization', WORDS.organization, sysAdmin),
    action('edit', WORDS.edit, IconEdit, { to: `${url}/edit`, show: roleOrgNode.accessRead, button: true }),
    action('user-permissions', WORDS.userPermissions, IconUser, { to: `${url}/user-permission`, show: permissions.users }),
    action('group-permissions', WORDS.groupPermissions, IconGroup, { to: `${url}/group-permission`, show: permissions.groups }),
    action('deep-link', WORDS.deepLink, IconLink, { onClick: () => host.openDeepLink(id), show: host.present && !!id }),
    action('move', WORDS.move, IconMove, { to: `${url}/move`, show: roleOrgNode.accessUpdate }),
    action('copy', WORDS.copy, IconCopy, { to: `${url}/copy`, show: roleOrgNode.accessUpdate && isCaffeine, button: true }),
    action('ai', WORDS.ai, IconAiDocument, { to: `${url}/ai`, show: featureAI }),
    action('new-template', WORDS.newTemplate, IconPlus, { to: `${url}/template`, disabled: !isCaffeine, show: roleOrgNode.accessCreate }),
    action('file-versions', WORDS.fileVersions, IconPublishing, { to: `${url}/edit/publishes`, show: roleOrgNode.accessRead }),
    history,
    action('delete', WORDS.delete, IconDelete, { to: `${url}/delete`, show: roleOrgNode.accessDelete, button: true })
  ]
}

/// ///////////////////////////////////////////////////////////////////////
// Folder (category): url = the folder's grid page; nodeID = the folder, or
// none for a section's top level (CONTENT, TEMPLATES, LIBRARY), whose header
// offers only the actions on what it holds.
//
// What a folder holds differs by section: Content folders take courses and
// sub-folders, Templates folders templates; the asset library's folders
// offer neither here, nor a recycle bin.

export const FOLDER_SECTION = { CONTENT: 'content', TEMPLATE: 'template', LIBRARY: 'library' }

const FOLDER_HOLDS = {
  [FOLDER_SECTION.CONTENT]: { addNode: true, addCategory: true, recycleBin: true },
  [FOLDER_SECTION.TEMPLATE]: { addNode: true, addCategory: false, recycleBin: true },
  [FOLDER_SECTION.LIBRARY]: { addNode: false, addCategory: false, recycleBin: false }
}

export const useFolderActions = ({ url, nodeID, section }) => {
  const sysAdmin = useLoginSysAdmin()
  const roleOrgNode = useRoleOrgNode()
  const permissions = usePermissionPages()
  const caps = useAuditCapabilities()

  const holds = FOLDER_HOLDS[section] || FOLDER_HOLDS[FOLDER_SECTION.CONTENT]
  const isFolder = !!nodeID
  const history = useHistoryAction(url)

  return [
    heading('internal', WORDS.internal, sysAdmin),
    action('sys-edit', WORDS.sysEdit, IconEdit, { to: `${url}/edit-sys`, show: sysAdmin && isFolder }),
    action('assets', WORDS.assets, IconFile, { to: `${url}/edit-sys/assets`, show: sysAdmin && isFolder }),
    action('add-node', WORDS.addNode, IconFile, { to: `${url}/add-type`, show: sysAdmin && holds.addNode }),

    heading('organization', WORDS.organization, sysAdmin),
    action('add-category', WORDS.addCategory, IconAdd, { to: `${url}/add`, show: holds.addCategory && roleOrgNode.accessCreate }),
    action('user-permissions', WORDS.userPermissions, IconUser, { to: `${url}/user-permission`, show: isFolder && permissions.users }),
    action('group-permissions', WORDS.groupPermissions, IconGroup, { to: `${url}/group-permission`, show: isFolder && permissions.groups }),
    action('edit', WORDS.edit, IconEdit, { to: `${url}/edit`, show: isFolder && roleOrgNode.accessRead, button: true }),
    action('move', WORDS.move, IconMove, { to: `${url}/move`, show: isFolder && roleOrgNode.accessUpdate }),
    action('file-versions', WORDS.fileVersions, IconPublishing, { to: `${url}/edit/publishes`, show: isFolder && roleOrgNode.accessRead }),
    action('recycle-bin', WORDS.recycleBin, IconRecycle, { to: `${url}/recycle`, show: holds.recycleBin && roleOrgNode.accessDelete }),
    isFolder ? history : action('audit-log', WORDS.auditLog, IconActivityLog, { to: `/org/audit-log${contentAuditSearch()}`, show: caps.canAudit }),
    action('delete', WORDS.delete, IconDelete, { to: `${url}/delete`, show: isFolder && roleOrgNode.accessDelete, button: true })
  ]
}

/// ///////////////////////////////////////////////////////////////////////
// Template: url = the template's page.

export const useTemplateActions = ({ url, node }) => {
  const sysAdmin = useLoginSysAdmin()
  const roleOrgNode = useRoleOrgNode()
  const permissions = usePermissionPages()
  const isCaffeine = useMetadataTypeIsCaffeine(node)
  const history = useHistoryAction(url)

  return [
    heading('internal', WORDS.internal, sysAdmin),
    action('sys-edit', WORDS.sysEdit, IconEdit, { to: `${url}/edit-sys`, show: sysAdmin }),
    action('assets', WORDS.assets, IconFile, { to: `${url}/edit-sys/assets`, show: sysAdmin }),
    action('version-metadata', WORDS.versionMetadata, IconFile, { to: `${url}/edit-sys/versions`, show: sysAdmin }),

    heading('organization', WORDS.organization, sysAdmin),
    action('edit', WORDS.edit, IconEdit, { to: `${url}/edit`, show: roleOrgNode.accessRead, button: true }),
    action('new-course', WORDS.newCourse, IconAdd, { to: `${url}/generate`, disabled: !isCaffeine, show: roleOrgNode.accessCreate }),
    action('move', WORDS.move, IconMove, { to: `${url}/move`, show: roleOrgNode.accessUpdate }),
    action('copy', WORDS.copy, IconCopy, { to: `${url}/copy`, disabled: !isCaffeine, show: roleOrgNode.accessCreate, button: true }),
    action('user-permissions', WORDS.userPermissions, IconUser, { to: `${url}/user-permission`, show: permissions.users }),
    action('group-permissions', WORDS.groupPermissions, IconGroup, { to: `${url}/group-permission`, show: permissions.groups }),
    action('file-versions', WORDS.fileVersions, IconPublishing, { to: `${url}/edit/publishes`, show: roleOrgNode.accessRead }),
    history,
    action('delete', WORDS.delete, IconDelete, { to: `${url}/delete`, show: roleOrgNode.accessDelete, button: true })
  ]
}

/// ///////////////////////////////////////////////////////////////////////
// Library item (asset library): url = the library item's page.

export const useLibraryActions = ({ url }) => {
  const sysAdmin = useLoginSysAdmin()
  const roleOrgNode = useRoleOrgNode()
  const history = useHistoryAction(url)

  return [
    heading('internal', WORDS.internal, sysAdmin),
    action('sys-edit', WORDS.sysEdit, IconEdit, { to: `${url}/edit-sys`, show: sysAdmin }),
    action('assets', WORDS.assets, IconFile, { to: `${url}/edit-sys/assets`, show: sysAdmin }),
    action('add-unity-package', WORDS.addUnityPackage, IconFile, { to: `${url}/add-unity-package`, show: sysAdmin }),

    heading('organization', WORDS.organization, sysAdmin),
    action('details', WORDS.details, IconDetails, { to: url, show: roleOrgNode.accessRead }),
    action('edit', WORDS.edit, IconEdit, { to: `${url}/edit`, show: roleOrgNode.accessUpdate, button: true }),
    action('move', WORDS.move, IconMove, { to: `${url}/move`, show: roleOrgNode.accessUpdate }),
    action('copy', WORDS.copy, IconCopy, { to: `${url}/copy`, show: roleOrgNode.accessUpdate, button: true }),
    action('user-permissions', WORDS.userPermissions, IconUser, { to: `${url}/user-permission`, show: roleOrgNode.accessRead }),
    action('group-permissions', WORDS.groupPermissions, IconGroup, { to: `${url}/group-permission`, show: roleOrgNode.accessRead }),
    action('file-versions', WORDS.fileVersions, IconPublishing, { to: `${url}/edit/publishes`, show: roleOrgNode.accessRead }),
    history,
    action('delete', WORDS.delete, IconDelete, { to: `${url}/delete`, show: roleOrgNode.accessDelete, button: true })
  ]
}

/// ///////////////////////////////////////////////////////////////////////
// Rendering. `place` is 'header' (a page's card header) or 'card' (a grid
// card's footer). The words are authored in sentence case ("Deep link",
// "AI", "Internal tools"), so the menu keeps them as written. `title` says
// why an item is disabled.

// Whether an action is pinned outside the ⋮ at a place, and kept there at
// phone width.
export const isPinnedAt = (a, place) => (place === 'header' ? !!a.button : place === 'card' ? !!a.card : false)
const keepsAtPhoneWidth = (a, place) => (place === 'header' ? !!a.primary : place === 'card' ? !!a.card : false)

export const NodeMenu = ({ actions, place, small }) => {
  return (
    <Menu sentenceCase small={small} squarePins={place === 'card'}>
      {actions.map(
        a =>
          a.heading !== undefined
            ? <MenuItemHeader key={a.id} text={a.heading} show={a.show} />
            : <MenuItem
                key={a.id}
                Icon={a.Icon}
                text={a.text}
                to={a.to}
                onClick={a.onClick}
                disabled={a.disabled}
                title={a.title}
                show={a.show}
                pin={isPinnedAt(a, place)}
                keepOnMobile={keepsAtPhoneWidth(a, place)}
              />
      )}
    </Menu>
  )
}

export const CourseMenu = ({ place, url, node, nodeID }) => <NodeMenu place={place} actions={useCourseActions({ url, node, nodeID })} />

export const FolderMenu = ({ place, url, nodeID, section }) =>
  <NodeMenu place={place} small={place === 'card'} actions={useFolderActions({ url, nodeID, section })} />

export const TemplateMenu = ({ place, url, node }) => <NodeMenu place={place} actions={useTemplateActions({ url, node })} />

export const LibraryMenu = ({ place, url }) => <NodeMenu place={place} actions={useLibraryActions({ url })} />
